/**
 * Solid scenery catalogue. In an open world anything that *looks* solid is solid, so hazards and
 * landmark scenery share one table. Colliders are authored in unit scale, in the object's local
 * frame (x = right, z = back, matching three.js), and multiplied by its uniform scale. Visual
 * prefabs in @cloudpearl/assets are built to the same dimensions: what you see is what you hit.
 */
export type HazardKind =
  // Sky Isles
  | 'boulder'
  | 'mossBoulder'
  | 'tree'
  | 'pine'
  | 'pillar'
  | 'gate'
  | 'house'
  | 'ruin'
  // Sunscar Dunes
  | 'sandstone'
  | 'arch'
  | 'spire'
  | 'cactus'
  | 'deadTree'
  | 'mesa';

export type ColliderPart =
  | { type: 'cyl'; dx: number; dz: number; r: number; y0: number; y1: number }
  | { type: 'box'; dx: number; dz: number; hw: number; hd: number; y0: number; y1: number };

export interface HazardDef {
  kind: HazardKind;
  /** Human readable — used for "taken out by a …" crash copy. */
  label: string;
  colliders: ColliderPart[];
  /** Number of visual variants the asset package builds. */
  variants: number;
  /** Rough radius (unit scale) used for spacing and clearance. */
  footprint: number;
  material: 'stone' | 'wood' | 'plant';
}

const cyl = (r: number, y1: number, y0 = 0, dx = 0, dz = 0): ColliderPart => ({ type: 'cyl', dx, dz, r, y0, y1 });
const box = (hw: number, hd: number, y0: number, y1: number, dx = 0, dz = 0): ColliderPart => ({ type: 'box', dx, dz, hw, hd, y0, y1 });

export const HAZARDS: Record<HazardKind, HazardDef> = {
  boulder: { kind: 'boulder', label: 'boulder', colliders: [cyl(0.88, 1.5)], variants: 5, footprint: 1.1, material: 'stone' },
  mossBoulder: { kind: 'mossBoulder', label: 'mossy boulder', colliders: [cyl(0.88, 1.45)], variants: 4, footprint: 1.1, material: 'stone' },
  tree: { kind: 'tree', label: 'tree', colliders: [cyl(0.45, 3.4), cyl(2.3, 9.5, 3.2)], variants: 3, footprint: 2.4, material: 'wood' },
  pine: { kind: 'pine', label: 'pine', colliders: [cyl(0.4, 2.2), cyl(1.6, 11, 1.8)], variants: 2, footprint: 1.7, material: 'wood' },
  pillar: { kind: 'pillar', label: 'ruined pillar', colliders: [cyl(0.85, 6.2)], variants: 3, footprint: 1.0, material: 'stone' },
  gate: {
    kind: 'gate',
    label: 'stone gate',
    colliders: [cyl(0.95, 7.4, 0, -4.6), cyl(0.95, 7.4, 0, 4.6), box(5.6, 0.85, 5.6, 7.6)],
    variants: 1,
    footprint: 6.0,
    material: 'stone',
  },
  house: { kind: 'house', label: 'cottage', colliders: [box(2.6, 2.5, 0, 7)], variants: 2, footprint: 3.4, material: 'stone' },
  ruin: { kind: 'ruin', label: 'ruined wall', colliders: [box(3.6, 0.9, 0, 3.2)], variants: 2, footprint: 3.8, material: 'stone' },

  sandstone: { kind: 'sandstone', label: 'sandstone boulder', colliders: [cyl(0.95, 1.6)], variants: 5, footprint: 1.15, material: 'stone' },
  arch: {
    kind: 'arch',
    label: 'sandstone arch',
    colliders: [cyl(1.9, 13.5, 0, -6.4), cyl(1.9, 13.5, 0, 6.4), box(8.6, 1.6, 10.2, 15.0)],
    variants: 2,
    footprint: 8.6,
    material: 'stone',
  },
  spire: { kind: 'spire', label: 'hoodoo', colliders: [cyl(1.45, 11.5)], variants: 3, footprint: 1.6, material: 'stone' },
  cactus: { kind: 'cactus', label: 'cactus', colliders: [cyl(0.45, 4.6), cyl(1.25, 3.4, 1.6)], variants: 3, footprint: 1.3, material: 'plant' },
  deadTree: { kind: 'deadTree', label: 'dead tree', colliders: [cyl(0.5, 5.8)], variants: 2, footprint: 0.9, material: 'wood' },
  // Stepped to follow the prefab's flared, tiered profile (r 50 buried → 45 at the base → 26.5 on top).
  mesa: {
    kind: 'mesa',
    label: 'mesa',
    colliders: [cyl(49, -7, -20), cyl(47, 0, -7), cyl(44, 6, 0), cyl(38.5, 12, 6), cyl(33.5, 36, 12), cyl(30.5, 47, 36)],
    variants: 3,
    footprint: 50,
    material: 'stone',
  },
};
